import {
  InteractionRequiredAuthError,
  type AccountInfo,
} from '@azure/msal-browser';
import { msalInstance, loginRequest, tokenRequest } from '../auth/msalConfig';
import { trackException } from '../lib/telemetry';
import { getErrorCode } from '../types/common';
import type { ProblemDetails } from '../types/common';

const BASE_URL = `${import.meta.env.VITE_FUNCTION_APP_URL}/api`;

/**
 * Thrown when a Foxit Commerce API call returns a 4xx/5xx status. Wraps the RFC 9457
 * problem details body and exposes the legacy `code` / `details` accessors derived from
 * the new envelope members.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly problem: ProblemDetails,
  ) {
    super(problem.detail ?? `API Error ${status}: ${getErrorCode(problem)}`);
    this.name = 'ApiError';
  }

  /** Machine-readable code derived from the `type` URI suffix (e.g. `VALIDATION_ERROR`). */
  get code(): string {
    return getErrorCode(this.problem);
  }

  /** Human-readable detail (alias of `problem.detail`). */
  get details(): string | undefined {
    return this.problem.detail;
  }

  /** Per-field or per-occurrence error messages (extension member). */
  get errors(): string[] | undefined {
    return this.problem.errors;
  }
}

function getActiveAccountClaims(): {
  email?: string;
  name?: string;
} {
  const account = msalInstance.getActiveAccount();
  if (!account) return {};

  const claims = account.idTokenClaims;

  const claimEmail = typeof claims?.email === 'string' ? claims.email : undefined;
  const claimName = typeof claims?.name === 'string' ? claims.name : undefined;
  const email = claimEmail ?? account.username;
  const name = claimName ?? account.name ?? undefined;

  const result: { email?: string; name?: string } = {};
  if (email !== undefined) result.email = email;
  if (name !== undefined) result.name = name;
  return result;
}

/**
 * Single-flight slot for the interactive-redirect fallback in
 * {@link getAuthHeaders}. `acquireTokenRedirect` navigates the page, and MSAL
 * throws `BrowserAuthError` (`interaction_in_progress`) when a second
 * interactive call starts while one is mid-flight. Pages that fan out
 * parallel authenticated fetches (e.g. `ApiDetail` mounting the API-list
 * query and the spec fetch together, compounded by React Query retries) race
 * their `InteractionRequiredAuthError` fallbacks into exactly that error, so
 * the redirect is initiated at most once per document lifetime and every
 * concurrent caller awaits the same initiation.
 *
 * If initiation itself fails (no navigation began), the slot is cleared so a
 * later request can attempt recovery, and the failure is reported to App
 * Insights instead of replacing the caller's original auth error.
 */
let redirectInitiation: Promise<void> | null = null;

function beginTokenRedirect(account: AccountInfo): Promise<void> {
  redirectInitiation ??= msalInstance
    .acquireTokenRedirect({
      ...loginRequest,
      account,
    })
    .catch((redirectError: unknown) => {
      redirectInitiation = null;
      trackException(
        redirectError instanceof Error
          ? redirectError
          : new Error(String(redirectError)),
        'apiClient',
        { operation: 'acquireTokenRedirect' },
      );
    });
  return redirectInitiation;
}

/**
 * Resolve the Bearer token + ancillary identity headers for an outbound API
 * call.
 *
 * Token-acquisition error handling:
 *
 *   * `InteractionRequiredAuthError` — the cached token is gone or the user's
 *     session no longer satisfies MSAL's silent flow (refresh token expired,
 *     conditional access challenge, password change, etc.). The MSAL
 *     reference pattern is to fall back to an interactive flow; we use
 *     `acquireTokenRedirect` because the portal already runs the redirect
 *     login pattern, serialized through {@link beginTokenRedirect} so
 *     concurrent callers cannot trip MSAL's `interaction_in_progress` guard.
 *     The redirect triggers a full-page navigation — callers observing the
 *     rejected promise should treat it as terminal for the current request
 *     lifecycle.
 *
 *   * All other errors — network failure, MSAL configuration error, etc. —
 *     are forwarded to App Insights via {@link trackException} so the
 *     silent-auth-failure visibility gap is closed, then rethrown so the
 *     caller can surface the failure (typically as an `ApiError`-equivalent
 *     at the call site).
 *
 * On both branches the original error is preserved; this function does not
 * swallow exceptions.
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  const account = msalInstance.getActiveAccount();
  if (!account) return {};

  let accessToken: string;
  try {
    const response = await msalInstance.acquireTokenSilent({
      ...tokenRequest,
      account,
    });
    accessToken = response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Surface a redirect on the way out so the user lands back at the
      // portal with a fresh session. Initiation is single-flight (see
      // beginTokenRedirect); we rethrow so the caller's promise chain
      // reflects the auth failure.
      await beginTokenRedirect(account);
      throw error;
    }
    trackException(error instanceof Error ? error : new Error(String(error)), 'apiClient', {
      operation: 'acquireTokenSilent',
    });
    throw error;
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
  };

  // X-Apim-User-Email / X-Apim-User-Name header contract.
  //
  // Purpose: telemetry / audit only. The Foxit Commerce Functions app's
  // `TelemetryMiddleware` captures `CallerId` and `ExternalSystem` from the
  // validated JWT; these two
  // `X-Apim-*` headers are forwarded as ancillary user-identity
  // breadcrumbs surfaced in App Insights `customDimensions` for support
  // triage. They are user-controllable claims sourced from MSAL's
  // `idTokenClaims` (`email`, `name`) with a fallback to `account.username` /
  // `account.name`.
  //
  // Security invariant — backend MUST NOT use these headers for any
  // authentication or authorization decision. The Bearer token in
  // `Authorization` is the sole identity source; `CallerContext` /
  // `OpsContext` derive ALL trust-bearing claims from JWT validation in
  // `JwtValidationMiddleware`. Treating these headers as authoritative would
  // create a header-spoofing path equivalent to skipping JWT validation.
  //
  // If the Functions team confirms these headers are not consumed
  // downstream, remove the attachment site — they are inert otherwise.
  const { email, name } = getActiveAccountClaims();
  if (email) headers['X-Apim-User-Email'] = email;
  if (name) headers['X-Apim-User-Name'] = name;

  return headers;
}

export interface FetchOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>;
  skipAuth?: boolean;
  idempotencyKey?: string;
}

export async function apiFetch<T>(
  path: string,
  options: FetchOptions = {},
): Promise<T> {
  const { skipAuth, idempotencyKey, ...fetchOptions } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(skipAuth ? {} : await getAuthHeaders()),
    ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
    ...options.headers,
  };

  const response = await fetch(`${BASE_URL}${path}`, {
    ...fetchOptions,
    headers,
  });

  if (!response.ok) {
    const errorBody = (await response.json().catch(() => ({}))) as Partial<ProblemDetails>;
    // Project-owned sentinel `type` URI for responses that omit `type`. The
    // HTTP status is preserved on `ApiError.status` / `problem.status`, so the
    // synthetic URI does not need to encode it. `urn:foxit:commerce:errors:unknown`
    // is disjoint from every entry in the documented BackendError code set.
    // Optional members are spread conditionally so `exactOptionalPropertyTypes`
    // does not reject `field: undefined` assignments to `field?: T`.
    const problem: ProblemDetails = {
      type: errorBody.type ?? 'urn:foxit:commerce:errors:unknown',
      title: errorBody.title ?? response.statusText,
      status: errorBody.status ?? response.status,
      ...(errorBody.detail !== undefined && { detail: errorBody.detail }),
      ...(errorBody.instance !== undefined && { instance: errorBody.instance }),
      ...(errorBody.errors !== undefined && { errors: errorBody.errors }),
      ...(errorBody.timestamp !== undefined && { timestamp: errorBody.timestamp }),
    };
    throw new ApiError(response.status, problem);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export function buildQueryString(
  params: Record<string, string | number | boolean | undefined>,
): string {
  const entries = Object.entries(params).filter(
    (entry): entry is [string, string | number | boolean] =>
      entry[1] !== undefined,
  );
  if (entries.length === 0) return '';
  const searchParams = new URLSearchParams();
  for (const [key, value] of entries) {
    searchParams.set(key, String(value));
  }
  return `?${searchParams.toString()}`;
}
